"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import WorkflowCanvas from "./WorkflowCanvas";

type DeviceType = "phone" | "desktop";

interface Scenario {
  id: string;
  device: DeviceType;
  activeNodes: readonly string[];
  activeEdges: readonly string[];
  packetEdges: readonly string[];
  badge: string;
  title: string;
  desc: string;
  chain: readonly string[];
  result: string;
}

const SCENARIOS: Scenario[] = [
  {
    id: "ios",
    device: "phone",
    activeNodes: ["trigger", "switch", "ios"],
    activeEdges: ["trigger>switch", "switch>ios"],
    packetEdges: ["trigger>switch", "switch>ios"],
    badge: "iPhone · US",
    title: "iOS visitor → App Store",
    desc: "Detected iOS, so the visitor skips the website and lands directly on your App Store listing.",
    chain: ["on_visit", "device.os = iOS", "App Store"],
    result: "App Store",
  },
  {
    id: "android",
    device: "phone",
    activeNodes: ["trigger", "switch", "android"],
    activeEdges: ["trigger>switch", "switch>android"],
    packetEdges: ["trigger>switch", "switch>android"],
    badge: "Pixel · BR",
    title: "Android visitor → Google Play",
    desc: "An Android user agent routes straight to your Play Store listing — no website detour.",
    chain: ["on_visit", "device.os = Android", "Google Play"],
    result: "Google Play",
  },
  {
    id: "de",
    device: "desktop",
    activeNodes: ["trigger", "switch", "geo", "de"],
    activeEdges: ["trigger>switch", "switch>geo", "geo>de"],
    packetEdges: ["trigger>switch", "switch>geo", "geo>de"],
    badge: "Desktop · DE",
    title: "German desktop → localized page",
    desc: "Not a mobile OS, so it flows to the region router — a German IP lands on your localized page.",
    chain: ["on_visit", "desktop", "geo = DE", "German page"],
    result: "German page",
  },
  {
    id: "web",
    device: "desktop",
    activeNodes: ["trigger", "switch", "geo", "web"],
    activeEdges: ["trigger>switch", "switch>geo", "geo>web"],
    packetEdges: ["trigger>switch", "switch>geo", "geo>web"],
    badge: "Desktop · US",
    title: "Everyone else → web fallback",
    desc: "No device or region rule matches, so the visitor gets your default responsive website.",
    chain: ["on_visit", "desktop", "else", "Web fallback"],
    result: "Web fallback",
  },
];

const GLYPH_TONE: Record<string, string> = {
  ios: "bg-[#f3f3f5] text-[#111]",
  android: "bg-data-green/15 text-data-green",
  de: "bg-white border border-line",
  web: "bg-brand-600/[0.08] text-brand-800",
};

function glyphSvg(id: string): ReactNode {
  switch (id) {
    case "ios":
      return (
        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
          <path d="M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-2.06-.92-3.39-.89-1.74.03-3.35 1.01-4.25 2.57-1.81 3.14-.46 7.79 1.3 10.34.86 1.25 1.88 2.65 3.22 2.6 1.29-.05 1.78-.83 3.34-.83 1.56 0 2 .83 3.37.81 1.39-.03 2.27-1.27 3.12-2.53.98-1.45 1.39-2.85 1.41-2.92-.03-.01-2.71-1.04-2.74-4.13M14.6 4.42c.71-.86 1.19-2.06 1.06-3.25-1.02.04-2.26.68-2.99 1.54-.66.76-1.23 1.97-1.08 3.13 1.14.09 2.3-.58 3.01-1.42" />
        </svg>
      );
    case "android":
      return (
        <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
          <path d="M4 2.5v19l16-9.5z" />
        </svg>
      );
    case "de":
      return (
        <svg viewBox="0 0 24 16" width="18" height="12">
          <rect width="24" height="5.33" y="0" fill="#000" />
          <rect width="24" height="5.33" y="5.33" fill="#D00" />
          <rect width="24" height="5.34" y="10.66" fill="#FFCE00" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2}>
          <rect x="3" y="4" width="18" height="13" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      );
  }
}

function GlyphTile({ id }: { id: string }) {
  return (
    <span className={`grid h-7 w-7 place-items-center rounded-[8px] ${GLYPH_TONE[id]}`}>
      {glyphSvg(id)}
    </span>
  );
}

function DeviceMock({ type, children }: { type: DeviceType; children: ReactNode }) {
  if (type === "desktop") {
    return (
      <div className="relative h-[78px] w-[116px] rounded-[9px] border-[2.5px] border-ink bg-surface p-1.5">
        <div className="grid h-full place-items-center rounded-[5px] bg-surface-2">{children}</div>
        <span className="absolute -bottom-[13px] left-1/2 h-[9px] w-[42px] -translate-x-1/2 rounded-b-[6px] bg-ink" />
      </div>
    );
  }
  return (
    <div className="relative h-[108px] w-[60px] rounded-[14px] border-[2.5px] border-ink bg-surface p-1.5">
      <span className="absolute left-1/2 top-[7px] h-[3px] w-4 -translate-x-1/2 rounded-[2px] bg-ink" />
      <div className="grid h-full place-items-center rounded-[7px] bg-surface-2">{children}</div>
    </div>
  );
}

const FADE = { animation: "fadeIn 0.5s ease" } as const;
const CYCLE_MS = 5200;

export default function WorkflowDemo() {
  const [step, setStep] = useState(0);
  const [animate, setAnimate] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let inView = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        setAnimate(inView && !reduced);
      },
      { threshold: 0.25 },
    );
    observer.observe(el);

    const timer = reduced
      ? 0
      : window.setInterval(() => {
          if (inView) setStep((s) => s + 1);
        }, CYCLE_MS);

    return () => {
      observer.disconnect();
      if (timer) clearInterval(timer);
    };
  }, []);

  const scenario = SCENARIOS[step % SCENARIOS.length];

  return (
    <div ref={rootRef} className="flex flex-col gap-[18px]">
      {/* explanation panel — driven by the current scenario */}
      <div className="flex min-h-[156px] flex-col items-center gap-[26px] rounded-ref-lg border border-line bg-surface px-[26px] py-[22px] shadow-ref md:flex-row">
        <div key={`dev-${step}`} style={FADE} className="grid min-h-[120px] w-[120px] shrink-0 place-items-center">
          <DeviceMock type={scenario.device}>
            <GlyphTile id={scenario.id} />
          </DeviceMock>
        </div>

        <div key={`body-${step}`} style={FADE} className="min-w-0 flex-1 text-center md:text-left">
          <div className="mb-2 flex flex-wrap items-center justify-center gap-3 md:justify-start">
            <span className="whitespace-nowrap rounded-full bg-brand-600 px-[11px] py-1 text-xs font-bold text-white">
              {scenario.badge}
            </span>
            <span className="text-[19px] font-bold tracking-[-0.02em]">{scenario.title}</span>
          </div>
          <p className="max-w-[62ch] text-[14.5px] leading-[1.55] text-muted">{scenario.desc}</p>
          <div className="mt-3.5 flex flex-wrap justify-center gap-2 md:justify-start">
            {scenario.chain.map((label, i) => (
              <span key={label} className="flex items-center gap-2">
                {i > 0 && <span className="text-dim">→</span>}
                <span
                  className={`flex items-center gap-[7px] rounded-lg border px-[11px] py-[5px] font-jetbrains text-xs ${
                    i === scenario.chain.length - 1
                      ? "border-brand-600/30 bg-brand-600/[0.08] font-semibold text-brand-600"
                      : "border-line bg-surface-2 text-muted"
                  }`}
                >
                  {label}
                </span>
              </span>
            ))}
          </div>
        </div>

        <div
          key={`res-${step}`}
          style={FADE}
          className="shrink-0 border-line text-center md:min-w-[96px] md:border-l md:pl-[26px]"
        >
          <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-xl border border-line bg-surface-2">
            <GlyphTile id={scenario.id} />
          </div>
          <div className="text-[11px] uppercase tracking-[0.08em] text-dim">Result</div>
          <div className="mt-0.5 text-[13.5px] font-semibold">{scenario.result}</div>
        </div>
      </div>

      {/* the routing graph */}
      <WorkflowCanvas
        activeNodes={scenario.activeNodes}
        packetEdges={scenario.packetEdges}
        scenarioKey={String(step)}
        animate={animate}
      />
    </div>
  );
}
